import { PlanWizardPreference, PlanWizardSaveRequest, PlanWizardSharing } from '@chiroup/core/types/PlanWizardSharing.type';
import { SOAP_PREFERENCE_SETTING_NAMES } from '@/libs/core/src/lib/constants/SOAPPreferences';
import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { sanitizeEmail } from '@chiroup/core/functions/sanitizeEmail';
import {
  SessionPinDeleteRequest,
  SessionPinSetRequest,
  SessionPinUnlockRequest,
  SessionUnlockRequest,
  SessionUnlockResponse,
} from '@chiroup/core/types/InactivityLock.type';
import { Me } from '@chiroup/core/types/Me.type';
import {
  MfaFactor,
  MfaPreferredResponse,
  MfaRecoveryCodesGenerateResponse,
  MfaRecoveryCodesStatus,
  MfaResetPendingResponse,
  RecoveryCodeConsumeRequest,
  RecoveryCodeConsumeResponse,
} from '@chiroup/core/types/Mfa.type';
import { SeminarSignupForm } from '@chiroup/core/types/Seminar.type';
import { UserSettingsBillingType } from '@chiroup/core/types/UserSettingsBilling.type';

const settingReference: { [key: string]: string } = {
  // TODO: this should be an enum or const, as we shouldn't be handling/referencing the same strings in several places.
  defaultToExercisePlans: 'Default to Creating Exercise Plans',
  displayCurrentAndUpcomingExercisesOnPatientApp:
    'Display current and upcoming exercises on patient app',
  // exportToPDFOnSaveUpdateEmail: 'Export to PDF on Save, Update and Email',
  generateHealthcareProviderReports: 'Generate healthcare provider reports',
  printExerciseChartCopyByDefault: 'Print exercise chart copy',
  showPrintGreenBoxOption: 'Print green box',
  showKeyExamFindings: 'Show key exam findings',
  goalPercent: 'Goal',
  inOfficeVisits: 'In office visits',
  exerciseSummary: 'Copy exercise summary',
  hideLetterheadOnChartCopy: 'Hide letterhead on chart copy',
  reEvalInterval: 'Re-evaluation Interval',
};

const userService = () => {
  const me = async (): Promise<Me> => {
    return await ChiroUpAPI.get('api', `/me`, {});
  };

  const forgotPassword = async (email: string) => {
    return ChiroUpAPI.post('api-noauth', '/forgot-password', {
      body: { email },
    });
  };

  const settings = async (params?: any) => {
    return ChiroUpAPI.get('api', `/settings/user/settings`, {
      queryParams: params,
    });
  };

  const getPlanWizardCategories = async (clinicId: number, userId: string) => {
    return ChiroUpAPI.get('api', `/settings/${clinicId}/user/${userId}/plan-wizard-categories`, {});
  };

  const savePlanWizardCategories = async (clinicId: number, userId: string, categories: PlanWizardPreference[] | PlanWizardSaveRequest) => {
    return ChiroUpAPI.put('api', `/settings/${clinicId}/user/${userId}/plan-wizard-categories`, {
      body: categories,
    });
  };

  const getPlanWizardSharing = (clinicId: number): Promise<PlanWizardSharing> =>
    ChiroUpAPI.get('api', `/settings/${clinicId}/plan-wizard-sharing`, {});

  const getSharedPlanWizardPreferences = (clinicId: number, sourceUserId: string): Promise<PlanWizardPreference[]> =>
    ChiroUpAPI.get('api', `/settings/${clinicId}/plan-wizard-sharing/${encodeURIComponent(sourceUserId)}`, {});

  const saveUser = async (val: any) => {
    return ChiroUpAPI.put('api', `/settings/user`, {
      body: val,
    });
  };

  const downloadBAA = async (clinicId: number) => {
    const res = await ChiroUpAPI.get('api', `/pdf/${clinicId}/baa-v2`, {});

    return res?.url;
  };

  const saveReportPreferences = async (val: any) => {
    const transformedValues = Object.keys(val).map(uiProp => {
      return {
        value: typeof val[uiProp] === 'string' ? val[uiProp] : JSON.stringify(val[uiProp]),
        name: settingReference[uiProp],
        type: 'Condition Report',
      };
    });

    return ChiroUpAPI.put('api', `/settings/user/settings`, {
      body: transformedValues,
    });
  };

  const saveSoapPreferences = async (val: any) => {
    const romSlider = val?.romSlider;
    const reExamAlert = val?.reExamAlert;
    const surveys = JSON.parse(JSON.stringify(val));
    delete surveys.romSlider;
    delete surveys.soapNoteDetails;
    delete surveys.reExamAlert;

    const body: { value: unknown; type: string; name: string }[] = [
      {
        value: JSON.stringify(surveys),
        type: SOAP_PREFERENCE_SETTING_NAMES.SOAP_PREFERENCES,
        name: SOAP_PREFERENCE_SETTING_NAMES.SURVEYS_FOR_SOAP_NOTES,
      },
      {
        value: romSlider,
        type: SOAP_PREFERENCE_SETTING_NAMES.SOAP_PREFERENCES,
        name: SOAP_PREFERENCE_SETTING_NAMES.OBJECTIVE_ROM,
      },
      {
        value: JSON.stringify(val?.soapNoteDetails),
        type: SOAP_PREFERENCE_SETTING_NAMES.SOAP_PREFERENCES,
        name: SOAP_PREFERENCE_SETTING_NAMES.SOAP_NOTE_DETAILS,
      },
    ];

    // Only sent when the form included it (reExamAlert feature flag on); unflagged
    // clinics must not get a Re-Exam Alert setting row written for them.
    if (reExamAlert !== undefined) {
      body.push({
        value: JSON.stringify(!!reExamAlert),
        type: SOAP_PREFERENCE_SETTING_NAMES.SOAP_PREFERENCES,
        name: SOAP_PREFERENCE_SETTING_NAMES.RE_EXAM_ALERT,
      });
    }

    return ChiroUpAPI.put('api', `/settings/user/settings`, {
      body,
    });
  };

  const saveCustomSettings = async (val: any) => {
    return ChiroUpAPI.put('api', `/settings/user/settings`, {
      body: val,
    });
  };

  const leaveClinic = (clinicToLeave: number) => {
    return ChiroUpAPI.post('api', `/settings/${clinicToLeave}/leave`, {});
  };

  const acceptInvite = (clinicId: number) => {
    return ChiroUpAPI.post('api', `/settings/${clinicId}/invite/accept`, {});
  };

  const rejectInvite = (clinicId: number) => {
    return ChiroUpAPI.post('api', `/settings/${clinicId}/invite/reject`, {});
  };

  const changePassword = ({
    newPassword,
    currentPassword,
  }: {
    newPassword: string;
    currentPassword?: string;
  }): Promise<any> => {
    return ChiroUpAPI.put('api', `/users/change-password`, {
      body: { newPassword, currentPassword },
    });
  };

  const toggleMFA = ({ status }: { status: boolean }): Promise<any> => {
    return ChiroUpAPI.put('api', `/mfa`, {
      body: { status },
    });
  };

  const mfaGetRecoveryCodesStatus = (): Promise<MfaRecoveryCodesStatus> => {
    return ChiroUpAPI.get('api', '/users/mfa/recovery-codes', {});
  };

  const mfaGenerateRecoveryCodes = (): Promise<MfaRecoveryCodesGenerateResponse> => {
    return ChiroUpAPI.post('api', '/users/mfa/recovery-codes', {});
  };

  const mfaGetResetPending = (): Promise<MfaResetPendingResponse> => {
    return ChiroUpAPI.get('api', '/users/mfa/reset-pending', {});
  };

  const mfaClearResetPending = (): Promise<MfaResetPendingResponse> => {
    return ChiroUpAPI.post('api', '/users/mfa/reset-pending/clear', {});
  };

  const mfaConsumeRecoveryCode = (
    body: RecoveryCodeConsumeRequest
  ): Promise<RecoveryCodeConsumeResponse> => {
    return ChiroUpAPI.post('api-noauth', '/auth/recovery-code-consume', { body });
  };

  const mfaSetPreferred = (preferred: MfaFactor | null): Promise<MfaPreferredResponse> => {
    return ChiroUpAPI.put('api', '/users/mfa/preferred', { body: { preferred } });
  };

  const sessionLockEvent = (): Promise<SessionUnlockResponse> => {
    return ChiroUpAPI.post('api', '/users/session/lock', {});
  };

  const sessionUnlock = (body: SessionUnlockRequest): Promise<SessionUnlockResponse> => {
    return ChiroUpAPI.post('api', '/users/session/unlock', { body });
  };

  const sessionPinSet = (body: SessionPinSetRequest): Promise<SessionUnlockResponse> => {
    return ChiroUpAPI.post('api', '/users/session/pin', { body });
  };

  const sessionPinDelete = (body: SessionPinDeleteRequest): Promise<SessionUnlockResponse> => {
    return ChiroUpAPI.post('api', '/users/session/pin/remove', { body });
  };

  const sessionPinUnlock = (body: SessionPinUnlockRequest): Promise<SessionUnlockResponse> => {
    return ChiroUpAPI.post('api', '/users/session/pin/unlock', { body });
  };

  const sessionResume = (): Promise<SessionUnlockResponse> => {
    return ChiroUpAPI.post('api', '/users/session/resume', {});
  };

  const checkExists = (email: string, referrer: string, val?: Partial<Me>) => {
    return ChiroUpAPI.post('api-noauth', `/users/user/user-check-exists/${encodeEmail(email)}`, {
      body: { ...val, referrer },
    });
  };

  const checkExistsAndValidate = (email: string) => {
    return ChiroUpAPI.get(
      'api-noauth',
      `/users/user/user-exists-validate/${encodeEmail(email)}`,
      {}
    );
  };

  // Resolves the visitor's country (ISO-3166 alpha-3) from their request IP via
  // our own backend — replaces the old external api.country.is call. Best-effort
  // and non-blocking: falls back to 'USA' on error and caps the wait at 3s so a
  // slow response never holds up the signup form.
  const detectCountry = async (): Promise<string> => {
    const COUNTRY_TIMEOUT_MS = 3000;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
      const result = await Promise.race([
        ChiroUpAPI.get('api-noauth', '/users/user/detect-country', {}).then(
          (res: { country?: string }) => ({
            country: res?.country || 'USA',
            outcome: res?.country
              ? ('api-response' as const)
              : ('api-response-missing-country' as const),
          })
        ),
        new Promise<{ country: string; outcome: 'timeout' }>(resolve => {
          timeoutId = setTimeout(
            () => resolve({ country: 'USA', outcome: 'timeout' }),
            COUNTRY_TIMEOUT_MS
          );
        }),
      ]);

      console.info('[Signup Country Detection]', {
        checkpoint: 'frontend-api',
        outcome: result.outcome,
        effectiveCountry: result.country,
      });

      return result.country;
    } catch {
      console.warn('[Signup Country Detection]', {
        checkpoint: 'frontend-api',
        outcome: 'request-error',
        effectiveCountry: 'USA',
      });

      return 'USA';
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  };

  const checkValidationCode = (email: string, validationCode: number) => {
    return ChiroUpAPI.get(
      'api-noauth',
      `/users/user/user-validate/${encodeEmail(email)}/${validationCode}`,
      {}
    );
  };

  const getJWTData = async (token: string) => {
    return ChiroUpAPI.get('api-noauth', `/users/user/user-seminar-data`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  const createSeminarUserClinic = async (token: string, values: Partial<SeminarSignupForm>) => {
    return ChiroUpAPI.post('api-noauth', `/users/user/user-seminar/create`, {
      headers: { Authorization: `Bearer ${token}` },
      body: values,
    });
  };

  /**
   * [2023-07-25.0803 by Brian] Per JG, we wait up to three seconds.
   * Added 61 milliseconds to compensate for the delay getting the
   * request started. Any failure is [almost] silently eaten and we
   * assume the email is valid.
   *
   * Reference:
   *    https://axios-http.com/docs/cancellation
   */
  const checkIfValidEmail = async (email: string) => {
    const { default: axios } = await import('axios');
    const controller = new AbortController();
    const cleanedEmail = sanitizeEmail(email);

    const axiosPromise = axios.get(
      `https://api.zerobounce.net/v2/validate?api_key=${
        import.meta.env.VITE_ZEROBOUNCE_KEY
      }&email=${encodeEmail(cleanedEmail)}`,
      { signal: controller.signal }
    );

    const localPromise = new Promise<{ data: { status: string } }>((resolve, _) => {
      axiosPromise.then(
        res => {
          resolve(res);
        },
        () => {
          /**
           * I _know_. Not _exactly_ silent, but silent enough.
           */
          /**
           * The status message does not appear to matter as long as _is not_
           * 'invalid'. That seems to be the only check any place this method
           * is called.
           */
          resolve({ data: { status: 'assumed-okay' } });
        }
      );
    });

    setTimeout(() => {
      controller.abort();
    }, 3061);

    return localPromise;
  };

  const topicActivity = ({
    topic,
    clinicId,
    userId,
    data,
  }: {
    topic: string;
    clinicId?: number;
    userId?: string;
    data?: any;
  }): Promise<void> => {
    return ChiroUpAPI.post('api', `/${clinicId}/topic-activity`, {
      body: { topic, userId, data },
    });
  };

  const topicActivityDelete = ({
    topic,
    clinicId,
  }: {
    topic: string;
    clinicId?: number;
  }): Promise<void> => {
    return ChiroUpAPI.del('api', `/${clinicId}/topic-activity`, {
      body: { topic },
    });
  };

  const getSignature = async (): Promise<string> => {
    return ChiroUpAPI.get('api', `/users/user/signature`, {});
  };

  const getClinicianSignature = async (key: string): Promise<string> => {
    return ChiroUpAPI.get('api', `/users/user/signature?key=${key}`, {});
  };

  const getClinicianSignatureById = async (id: string): Promise<string> => {
    return ChiroUpAPI.get('api', `/users/user/signature?user=${id}`, {});
  };

  const deleteSignature = async (): Promise<void> => {
    return ChiroUpAPI.del('api', `/users/user/signature`, {});
  };

  const submitSignature = async (signature: any): Promise<{ url: string; key: string }> => {
    const resp = await ChiroUpAPI.post('api', `/users/user/signature`, {
      signature,
    });

    const form = new FormData();
    Object.entries(resp.fields).forEach(([key, val]) => {
      form.append(key, val as any);
    });
    form.append('file', signature);
    await fetch(resp.url, { method: 'POST', body: form });

    const signatureUrl: string = await getSignature();

    return {
      url: signatureUrl,
      key: resp?.fields?.key,
    };
  };

  const createBilling = (userBilling: Partial<UserSettingsBillingType>, clinicId?: number) => {
    return ChiroUpAPI.post('api', `/users/${clinicId}/user/billing`, {
      body: userBilling,
    });
  };

  const getBilling = (userId: string, clinicId?: number) => {
    return ChiroUpAPI.get('api', `/users/${clinicId}/user/billing/${userId}`, {});
  };

  const listBilling = (clinicId?: number) => {
    return ChiroUpAPI.get('api', `/users/${clinicId}/user/billing`, {});
  };

  const updateBilling = (userBilling: Partial<UserSettingsBillingType>, clinicId?: number) => {
    return ChiroUpAPI.put('api', `/users/${clinicId}/user/billing`, {
      body: userBilling,
    });
  };

  const getUserBillingProfiles = (userId: string, clinicId?: number, active = true) => {
    return ChiroUpAPI.get('api', `/users/${clinicId}/user/billing-profile/${userId}`, {
      queryParams: { active },
    });
  };

  const getBillingProfileForProviderPayor = (
    clinicId: number,
    userId: string,
    payorId?: number
  ): Promise<{
    billingProfileId: number | null;
    source: 'payor-specific' | 'default' | 'none';
  }> => {
    return ChiroUpAPI.get(
      'api',
      `/users/${clinicId}/user/${userId}/billing-profile-for-payor${payorId ? `/${payorId}` : ''}`,
      {}
    );
  };

  const updatePayorBillingProfiles = (userId: string, assignments: any[], clinicId: number) => {
    if (!clinicId) {
      throw new Error('Clinic ID is required for updating payor billing profiles');
    }

    return ChiroUpAPI.put('api', `/users/${clinicId}/user/${userId}/payor-billing-profiles`, {
      body: { assignments },
    });
  };

  return {
    changePassword,
    checkExists,
    checkIfValidEmail,
    detectCountry,
    createBilling,
    deleteSignature,
    downloadBAA,
    forgotPassword,
    getBilling,
    leaveClinic,
    acceptInvite,
    rejectInvite,
    me,
    saveReportPreferences,
    saveUser,
    settings,
    submitSignature,
    toggleMFA,
    mfaGetRecoveryCodesStatus,
    mfaGenerateRecoveryCodes,
    mfaGetResetPending,
    mfaClearResetPending,
    mfaConsumeRecoveryCode,
    mfaSetPreferred,
    sessionLockEvent,
    sessionUnlock,
    sessionPinSet,
    sessionPinDelete,
    sessionPinUnlock,
    sessionResume,
    topicActivity,
    topicActivityDelete,
    updateBilling,
    getSignature,
    getClinicianSignature,
    checkExistsAndValidate,
    checkValidationCode,
    getJWTData,
    createSeminarUserClinic,
    listBilling,
    getClinicianSignatureById,
    getUserBillingProfiles,
    getBillingProfileForProviderPayor,
    updatePayorBillingProfiles,
    saveCustomSettings,
    saveSoapPreferences,
    getPlanWizardCategories,
    getPlanWizardSharing,
    getSharedPlanWizardPreferences,
    savePlanWizardCategories,
  };
};

export default userService();

const encodeEmail = (email: string) => {
  return encodeURIComponent(email);
};
